import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react"
import PropTypes from "prop-types"
import { useIntl, navigate, FormattedMessage } from "gatsby-plugin-intl"
import trackCustomEventTrigger from "utils/trackCustomEventTrigger"

import paths from "utils/paths"
import getPropertyTypeDescription from "utils/getPropertyTypeDescription"
import getProjectTypeDescription from "utils/getProjectTypeDescription"
import getStatusTypeDescription from "utils/getStatusTypeDescription"

import H from "components/atoms/H"

import {
  StyledWrapper,
  StyledIcon,
  StyledLink,
  headingStyles,
  StyledDropdownWrapper,
  StyledHeadingWrapper,
  StyledCount,
  StyledAutocomplete,
  StyledControlsWrapper,
  StyledStatusDropdown,
  StyledProjectTypeDropdown,
  StyledPropertyTypeDropdown,
  StyledFilterButton,
  StyledFilterHeading,
  StyledBackButton,
  StyledFiterHeadingWrapper,
  StyledResetWrapper,
  StyledResetButton,
  StyledButtonsWrapper,
} from "./PortfolioHeader.styles"

const PROPERTY_TYPES = [
  "ALL_TYPES",
  "MIXED_USE",
  "INDUSTRIAL_FLEX",
  "OFFICE_MEDICAL",
  "HOTEL",
  "RETAIL_FOOD",
  "RESIDENTIAL",
]

const PROJECT_TYPES = ["ALL_TYPES", "GROUND_UP", "VALUE_ADD", "CORE"]

const STATUS_TYPES = ["ALL_TYPES", "CURRENT", "PAST"]

const PortfolioHeader = forwardRef(
  (
    {
      onChange,
      count,
      icon,
      projects,
      defaultPropertyType,
      defaultProjectType,
    },
    ref
  ) => {
    const { portfolioProjectSnapshot } = paths
    const { formatMessage } = useIntl()
    const [selectedPropertyType, setSelectedPropertyType] = useState({
      label: getPropertyTypeDescription(PROPERTY_TYPES[0]),
      value: PROPERTY_TYPES[0],
    })
    const [selectedProjectType, setSelectedProjectType] = useState({
      label: getProjectTypeDescription(PROJECT_TYPES[0]),
      value: PROJECT_TYPES[0],
    })
    const [selectedStatusType, setSelectedStatusType] = useState({
      label: getStatusTypeDescription(STATUS_TYPES[0]),
      value: PROJECT_TYPES[0],
    })
    const [autocompleteOptions, setAutocompleteOptions] = useState([])
    const [isFilterDrawerVisible, setFilterDrawerStatus] = useState(false)

    const propertiesOptions = PROPERTY_TYPES.map((type) => ({
      label: getPropertyTypeDescription(type),
      value: type,
    }))

    const projectOptions = PROJECT_TYPES.map((type) => ({
      label: getProjectTypeDescription(type),
      value: type,
    }))

    const statusOptions = STATUS_TYPES.map((type) => ({
      label: getStatusTypeDescription(type),
      value: type,
    }))

    useImperativeHandle(ref, () => ({
      // eslint-disable-next-line no-use-before-define
      resetFilters,
    }))

    useEffect(() => {
      if (defaultPropertyType) {
        const label = getPropertyTypeDescription(defaultPropertyType, true)
        setSelectedPropertyType({
          label: <FormattedMessage id={label} />,
          value: defaultPropertyType,
        })
      }
    }, [defaultPropertyType])

    useEffect(() => {
      if (defaultProjectType) {
        const label = getProjectTypeDescription(defaultProjectType, true)
        setSelectedProjectType({
          label: <FormattedMessage id={label} />,
          value: defaultProjectType,
        })
      }
    }, [defaultProjectType])

    useEffect(() => {
      onChange({
        projectType: selectedProjectType,
        propertyType: selectedPropertyType,
        statusType: selectedStatusType,
      })
    }, [selectedProjectType, selectedPropertyType, selectedStatusType])

    const getResultsTranslation = (projectCount) => {
      if (projectCount === 0) {
        return formatMessage({ id: "portfolioHeader.results.zero" })
      }
      if (projectCount === 1) {
        return formatMessage({ id: "portfolioHeader.results.one" })
      }
      return formatMessage({ id: "portfolioHeader.results.multiple" })
    }

    const handleSearch = (value) => {
      const filteredProjects = projects.filter(
        (project) =>
          project.label.toLowerCase().search(`${value.toLowerCase()}`) >= 0
      )
      setAutocompleteOptions(filteredProjects)
    }

    const handleSelectedPlace = (choosenOption) => {
      navigate(portfolioProjectSnapshot(choosenOption.value))
    }

    const openFilters = () => setFilterDrawerStatus(true)

    const closeFilters = () => setFilterDrawerStatus(false)

    const resetFilters = () => {
      setSelectedPropertyType({
        label: (
          <FormattedMessage
            id={getPropertyTypeDescription(PROPERTY_TYPES[0], true)}
          />
        ),
        value: PROPERTY_TYPES[0],
      })

      setSelectedProjectType({
        label: (
          <FormattedMessage
            id={getProjectTypeDescription(PROJECT_TYPES[0], true)}
          />
        ),
        value: PROJECT_TYPES[0],
      })

      setSelectedStatusType({
        label: (
          <FormattedMessage
            id={getStatusTypeDescription(STATUS_TYPES[0], true)}
          />
        ),
        value: PROJECT_TYPES[0],
      })
    }

    return (
      <StyledWrapper>
        <StyledHeadingWrapper>
          <H as="h3" extraStyles={headingStyles}>
            {formatMessage({ id: "portfolioHeader.heading" })}
          </H>
          <StyledCount data-testid="portfolioProjectCount">
            {`${count} ${getResultsTranslation(count)}`}
          </StyledCount>

          <StyledLink to={icon.pathToNavigate} mobile>
            <StyledIcon
              icon={icon.name}
              data-testid="portfolioMapToggleMobile"
            />
          </StyledLink>
        </StyledHeadingWrapper>

        <StyledControlsWrapper>
          <StyledAutocomplete
            onSelected={handleSelectedPlace}
            onSearch={handleSearch}
            options={autocompleteOptions}
            placeholder={formatMessage({
              id: "portfolioHeader.searchPlaceholder",
            })}
            info={formatMessage({ id: "portfolioHeader.searchInfo" })}
            direction="bottom"
          />
          <StyledFilterButton onClick={openFilters}>
            <StyledIcon
              icon="filter"
              data-testid="portfolioFilterDrawerToggle"
            />
          </StyledFilterButton>
          <StyledDropdownWrapper isVisible={isFilterDrawerVisible}>
            <StyledFiterHeadingWrapper>
              <StyledFilterHeading>
                {formatMessage({ id: "portfolioHeader.filters" })}
              </StyledFilterHeading>
              <StyledCount data-testid="portfolioProjectCount">
                {`${formatMessage({
                  id: "portfolioHeader.currently",
                })} ${count} ${getResultsTranslation(count)} `}
              </StyledCount>
            </StyledFiterHeadingWrapper>

            <StyledPropertyTypeDropdown
              options={propertiesOptions}
              onChange={setSelectedPropertyType}
              defaultValue={selectedPropertyType}
              direction="bottom"
            />
            <StyledProjectTypeDropdown
              options={projectOptions}
              onChange={setSelectedProjectType}
              defaultValue={selectedProjectType}
              direction="bottom"
            />
            <StyledStatusDropdown
              options={statusOptions}
              onChange={setSelectedStatusType}
              defaultValue={selectedStatusType}
              direction="bottom"
            />

            <StyledResetWrapper onClick={resetFilters}>
              <StyledIcon icon="reset" data-testid="portfolioFilterReset" />
            </StyledResetWrapper>

            <StyledButtonsWrapper>
              <StyledResetButton onClick={resetFilters} type="secondary">
                {formatMessage({ id: "portfolioHeader.reset" })}
              </StyledResetButton>
              <StyledBackButton onClick={closeFilters} type="primary">
                {formatMessage({ id: "portfolioHeader.back" })}
              </StyledBackButton>
            </StyledButtonsWrapper>
          </StyledDropdownWrapper>

          <StyledLink
            to={icon.pathToNavigate}
            onClick={() =>
              trackCustomEventTrigger({
                category: `Portfolio filter`,
                action: "Click",
                label: `Portfolio filter button(${icon.name})`,
              })
            }
          >
            <StyledIcon
              icon={icon.name}
              data-testid="portfolioMapToggleDesktop"
            />
          </StyledLink>
        </StyledControlsWrapper>
      </StyledWrapper>
    )
  }
)

PortfolioHeader.propTypes = {
  onChange: PropTypes.func.isRequired,
  count: PropTypes.number,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  icon: PropTypes.shape({
    name: PropTypes.string,
    pathToNavigate: PropTypes.string,
  }).isRequired,
  defaultPropertyType: PropTypes.string,
  defaultProjectType: PropTypes.string,
}

PortfolioHeader.defaultProps = {
  count: 0,
  defaultPropertyType: null,
  defaultProjectType: null,
}

export default PortfolioHeader
